import React, { useState } from 'react';

import { getUser } from '../actions/session';
import axiosInstance from '../config/axiosConfig';
import { Notifications } from '../components/common/notification';
import { withRouter } from 'react-router-dom';
import { USER_CREATE_PAYMENT_STRIPE } from '../config/rest_endpoints';

/**
 * StripePayment - Premium Stripe Payment Form
 *
 * Modal form for adding funds via Stripe with
 * beautiful styling and smooth interactions.
 */
const StripePayment = (props) => {
  const [state, setState] = useState({
    payment: ''
  });
  const [isLoad, setIsLoad] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onAmountSubmit = () => {
    if (!state.payment || parseFloat(state.payment) <= 0) {
      Notifications.notify('Please enter a valid amount', 'Error', 'error');
      return;
    }

    var userId = getUser();
    if (!userId || !userId.id) return;
    var payload = {
      amount: parseFloat(state.payment)
    };
    setIsLoad(true);
    // Using axiosInstance which automatically adds auth header and handles 401 errors
    axiosInstance.post(`${USER_CREATE_PAYMENT_STRIPE}/${userId.id}/payment/stripe/create`, payload)
      .then((res) => {
        if (res) {
          const resp = res.data;
          const url = resp.data;
          window.location.replace(url);
        }
      })
      .catch((err) => {
        setIsLoad(false);
        // 401/JWT expiry is handled by axiosInstance interceptor
        if (err.response && err.response.status !== 401) {
          const alert = err.response;
          if (alert.data && alert.data.message !== 'invalid or expired jwt') {
            Notifications.notify(alert.data.message, 'Failed', 'error');
          }
        }
      });
  };

  // Quick amount buttons
  const quickAmounts = [25, 50, 100, 250];

  return (
    <div style={{ padding: '0.5rem', animation: 'fadeIn 0.3s ease' }}>

      {/* Stripe Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
          borderRadius: '12px'
        }}
      >
        <i
          className="fab fa-stripe"
          style={{
            fontSize: '2rem',
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginRight: '0.75rem'
          }}
        ></i>
        <span style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--color-text)' }}>
          Stripe Payment
        </span>
      </div>

      {/* Quick Amount Buttons */}
      <div style={{ marginBottom: '1.25rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Quick Select
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {quickAmounts.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => setState({ payment: amount.toString() })}
              style={{
                flex: 1,
                padding: '0.625rem 0',
                borderRadius: '8px',
                border: state.payment === amount.toString() ? '2px solid #6366F1' : '1px solid var(--color-border)',
                background: state.payment === amount.toString() ? 'rgba(99, 102, 241, 0.1)' : 'var(--color-surface)',
                color: state.payment === amount.toString() ? '#6366F1' : 'var(--color-text)',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ${amount}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Amount Input */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          <i className="icon-coin-dollar" style={{ marginRight: '0.375rem' }}></i>
          Amount to Add
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '10px',
            border: '1px solid var(--color-border)',
            overflow: 'hidden',
            background: 'var(--color-surface)'
          }}
        >
          <span
            style={{
              padding: '0.75rem 1rem',
              background: 'var(--color-bg-subtle)',
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              fontSize: '1rem',
              borderRight: '1px solid var(--color-border)'
            }}
          >
            $
          </span>
          <input
            type="number"
            name="payment"
            value={state.payment}
            onChange={handleChange}
            placeholder="0.00"
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              border: 'none',
              outline: 'none',
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--color-text)',
              background: 'transparent'
            }}
          />
          <span
            style={{
              padding: '0.75rem 1rem',
              background: 'var(--color-bg-subtle)',
              color: 'var(--color-text-tertiary)',
              fontSize: '0.875rem',
              borderLeft: '1px solid var(--color-border)'
            }}
          >
            USD
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={onAmountSubmit}
        disabled={isLoad || !state.payment || parseFloat(state.payment) <= 0}
        style={{
          width: '100%',
          background:
            isLoad || !state.payment || parseFloat(state.payment) <= 0
              ? 'var(--color-bg-subtle)'
              : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
          border: 'none',
          borderRadius: '10px',
          padding: '0.875rem 1.5rem',
          color: isLoad || !state.payment || parseFloat(state.payment) <= 0 ? 'var(--color-text-tertiary)' : 'white',
          fontWeight: 600,
          fontSize: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          cursor: isLoad || !state.payment || parseFloat(state.payment) <= 0 ? 'not-allowed' : 'pointer',
          boxShadow:
            isLoad || !state.payment || parseFloat(state.payment) <= 0 ? 'none' : '0 4px 14px rgba(99, 102, 241, 0.3)'
        }}
      >
        {isLoad ? (
          <>
            <i className="icon-spinner2 spinner"></i>
            Processing...
          </>
        ) : (
          <>
            <i className="fab fa-stripe"></i>
            Pay with Stripe
            {state.payment && parseFloat(state.payment) > 0 && (
              <span style={{ opacity: 0.8 }}>(${parseFloat(state.payment).toFixed(2)})</span>
            )}
          </>
        )}
      </button>

      {/* Security Notice */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '1rem',
          color: 'var(--color-text-tertiary)',
          fontSize: '0.75rem'
        }}
      >
        <i className="icon-lock2"></i>
        <span>Secure payment via Stripe</span>
      </div>
    </div>
  );
};

export default withRouter(StripePayment);
